import React, { useState, useEffect } from 'react';
import { 
  Hash, 
  File, Database, 
  HardDrive, 
  Type, AlertCircle, Lock, Code, 
  Upload, Key,ShieldCheck,
  Copy, 
  Shield, Sliders,RotateCcw, BarChart, FileType,
  Check, 
  AlertTriangle,
  RefreshCw,
  Download,
  Info
} from 'lucide-react';
import { get_hashValue } from '../../services/api';
import IntegrityBoard from './HashMaster/IntergrityCheck';
import Footer from './HashMaster/Footer';
import HashVisualizer from './HashMaster/HashVisualizer';
import NavBar from './HashMaster/NavBar';


const HashingInterface = () => {
  const [activeTab, setActiveTab] = useState('hashboard'); 
  const [inputType, setInputType] = useState('text');
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('sha256');
  const [inputText, setInputText] = useState('');
  const [outputFormat, setOutputFormat] = useState('hex');
  const [isProcessing, setIsProcessing] = useState(false); 
  const [isCopiedInput, setIsCopiedInput] = useState(false);
  const [textColor, setTextColor] = useState('text-gray-500'); // State for text color
  const [hashResult, setHashResult] = useState('');
  const [isCopiedOutput, setIsCopiedOutput] = useState(false);
  const [salt, setSalt] = useState(false);
  const [inputFileName, setInputFileName] = useState('');
  const [useSalt, setUseSalt] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [outputBorderColor, setOutputBorderColor] = useState('bg-gray-900'); // Default
  const [inputBorderColor, setInputBorderColor] = useState('border-gray-700'); // Default
  const [saltBorderColor, setSaltBorderColor] = useState('border-gray-700'); // Default
  const [selectedDisk, setSelectedDisk] = useState(null);
  const [diskList, setDiskList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Fetch storage devices whenever the inputType changes to 'disk'
    const fetchStorageDevices = async () => {
      if (inputType === 'disk') {
        setLoading(true);
        try {
          const response = await fetch('/ls_volume');
          const data = await response.json();
          setDiskList(data.devices);

          if (data.devices.length === 0) {
            setMessage("No storage devices found.");
          } else {
            setMessage("");
          }
        } catch (error) {
          setMessage("Error fetching storage devices.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchStorageDevices(); // Call the function whenever inputType changes
  }, [inputType]);

   // Download Function
   const handleDownload = (text, filename) => {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Reset Function (Clears the output)
  const handleResetOutput = () => {
    setHashResult(""); // Reset output
  };


  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const notifications = [
    { id: 1, text: "New deployment successful", time: "2m ago", isNew: true },
    { id: 2, text: "System update scheduled", time: "1h ago", isNew: true },
    { id: 3, text: "Database backup completed", time: "3h ago", isNew: false },
  ];



  const algorithms = [
    { id: 'md5', name: 'MD5', security: 'Low' },
    { id: 'sha1', name: 'SHA-1', security: 'Medium' },
    { id: 'sha224', name: 'SHA-224', security: 'High' },
    { id: 'sha256', name: 'sha256', security: 'High' },
    { id: 'sha384', name: 'SHA-384', security: 'Very High' },
    { id: 'sha512', name: 'SHA-512', security: 'Very High' },
    { id: 'blake2b', name: 'BLAKE2b', security: 'Very High' },
    { id: 'blake2s', name: 'BLAKE2s', security: 'Very High' },
    { id: 'sha3-224', name: 'SHA3-224', security: 'High' },
    { id: 'sha3-256', name: 'SHA3-256', security: 'Very High' },
    { id: 'sha3-384', name: 'SHA3-384', security: 'Very High' },
    { id: 'sha3-512', name: 'SHA3-512', security: 'Very High' },
    { id: 'argon2', name: 'Argon2', security: 'Very High' },
    { id: 'bcrypt', name: 'Bcrypt', security: 'High' }, // Passlib algorithms
    { id: 'pbkdf2_sha256', name: 'PBKDF2-SHA256', security: 'High' }, // Passlib algorithms
    { id: 'pbkdf2_sha512', name: 'PBKDF2-SHA512', security: 'Very High' }, // Passlib algorithms
];

  const levels = ['Low', 'Medium', 'High', 'Very High'];

  const collisionResistance = {
    'md5': false,   // MD5 has been shown to have collisions
    'sha1': false,   // SHA-1 has been shown to have collisions
    'sha224': false, // SHA-224 is a variant of SHA-1 and has similar collision properties
    'sha256': false, // SHA-256 is currently secure but potentially vulnerable in a collision context.
    'sha384': false, // SHA-384 is currently secure but potentially vulnerable in a collision context.
    'sha512': false, // SHA-512 is currently secure but potentially vulnerable in a collision context.
    'blake2b': true,
    'blake2s': true,
    'argon2': true,
    'bcrypt': true,
    'pbkdf2_sha256': true,
    'pbkdf2_sha512': true,
    'sha3-224': true,
    'sha3-256': true,
    'sha3-384': true,
    'sha3-512': true,
};
  

useEffect(() => {
  setInputText('');
  setInputFileName('');
  setSelectedDisk(null)
}, [inputType]);

const submitHashing = async () => {
  // Check if the inputText is empty
  if (!inputText.trim()) {  
      setErrorMessage('Input cannot be empty.');
      setInputBorderColor('border-red-500'); 
      return;
  }
  

  // Reset error messages and input styles
  setErrorMessage('');
  setInputBorderColor('border-gray-700');
  setSaltBorderColor('border-gray-700');  
  setIsProcessing(true);
  setHashResult('');
  setOutputBorderColor('bg-gray-900'); 
  setTextColor('text-gray-500');

  try {
      const response = await get_hashValue(
          selectedAlgorithm, 
          inputText, // This now contains either text or file content
          salt, 
          useSalt, 
          outputFormat,
          inputType
      );

      // Handle response
      if (response.error) {
          setHashResult(response.error.message);       
          setOutputBorderColor('border-red-500');
          setTextColor('text-red-500'); // Error text color
      } else {
          setHashResult(response.result);   
          setOutputBorderColor('border-green-500');
          setTextColor('text-green-500'); // Success text color
      }
  } catch (error) {  
      console.error('Error hashing:', error);
      setOutputBorderColor('border-red-500');
      setHashResult('An unexpected error occurred. Please try again.');
      setTextColor('text-red-500'); // Generic error text color
  } finally {    
      setIsProcessing(false);
  }
};


const selectDisk = (device) => {
  setSelectedDisk(device); // Set the selected disk
  setInputText(device); // Set input text to the disk identifier or any relevant info
  setInputFileName(''); // Reset file name if something was previously selected
};



const handleFileChange = async (event) => {
  try {
    const file = event.target.files[0];
    const fileContent = await encodeFile(file);
    setInputText(fileContent);
  } catch (error) {
    console.error('Error reading file:', error);
    setErrorMessage('Error reading file.');
    setInputBorderColor('border-red-500');
  }
};

const encodeFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(btoa(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsBinaryString(file);
  });
};
  
  
const fallbackCopyTextToClipboard = (text) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed"; // avoid scrolling to bottom
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand("copy");
  } catch (err) {
    console.error("Fallback: Copy command failed", err);
  }

  document.body.removeChild(textArea);
};

const handleCopy = (text, setIsCopied) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch((err) => {
      console.error("Clipboard write failed: ", err);
      fallbackCopyTextToClipboard(text);
    });
  } else {
    fallbackCopyTextToClipboard(text);
  }
};

  // Reset Function
  const handleReset = () => {
    setInputText(""); // Clear input or reset state
  };

  

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      

      {/* Advanced Hashing Tool Interface */}
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 text-gray-100 font-sans">
        {/* Top Navigation Bar */}
        <NavBar 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        algorithms={algorithms}
        />

        {/* Main Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-6">            

        {/* Content based on the active tab */}
        {activeTab === 'hashboard' && (
           <div className="grid grid-cols-12 gap-6">
           {/* Left Panel */}
           <div className="col-span-3">
             <div className="space-y-6">
               {/* Algorithm Selection */}
               <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg">
                 <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800">
                   <h3 className="text-lg font-semibold flex items-center">
                     <Shield className="w-5 h-5 mr-2 text-blue-400" />
                     Hashing Algorithms
                   </h3>
                 </div>
                 <div className="p-3">
                   <div className="bg-black/30 rounded-lg p-1">
                     <div className="space-y-1 max-h-52 overflow-y-auto custom-scrollbar">
                       {algorithms.map((algo) => (
                         <button
                           key={algo.id}
                           onClick={() => setSelectedAlgorithm(algo.id)}
                           className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition-all ${
                             selectedAlgorithm === algo.id
                               ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                               : 'hover:bg-gray-800'
                           }`}
                         >
                           <div className="flex items-center">
                             {selectedAlgorithm === algo.id && (
                               <Check className="w-4 h-4 mr-2 text-blue-300" />
                             )}
                             <span>{algo.name}</span>
                           </div>
                           <span className={`text-xs px-2 py-1 rounded-full ${
                             algo.security === 'Low' ? 'bg-red-900/40 text-red-300 border border-red-700/30' :
                             algo.security === 'Medium' ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700/30' :
                             'bg-green-900/40 text-green-300 border border-green-700/30'
                           }`}>
                             {algo.security}
                           </span>
                         </button>
                       ))}
                     </div>
                   </div>
                 </div>
               </div>
     
               {/* Input Options */}
               <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg">
                 <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800">
                   <h3 className="text-lg font-semibold flex items-center">
                     <Upload className="w-5 h-5 mr-2 text-blue-400" />
                     Input Options
                   </h3>
                 </div>
                 <div className="p-3">
                   <div className="space-y-1">
                     <button
                       onClick={() => setInputType('text')}
                       className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-all ${
                         inputType === 'text' ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-md' : 'hover:bg-gray-800'
                       }`}
                     >
                       <Type className="w-4 h-4 mr-2" />
                       Text Input
                     </button>
                     <button
                       onClick={() => setInputType('file')}
                       className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-all ${
                         inputType === 'file' ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-md' : 'hover:bg-gray-800'
                       }`}
                     >
                       <File className="w-4 h-4 mr-2" />
                       File Input
                     </button>
                     <button
                       onClick={() => setInputType('disk')}
                       className={`w-full text-left px-3 py-2 rounded-lg flex items-center transition-all ${
                         inputType === 'disk' ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-md' : 'hover:bg-gray-800'
                       }`}
                     >
                       <HardDrive className="w-4 h-4 mr-2" />
                       Disk Image
                     </button>
                   </div>
                 </div>
               </div>
     
               {/* Advanced Options Panel */}
               <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg">
                 <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800">
                   <h3 className="text-lg font-semibold flex items-center">
                     <Sliders className="w-5 h-5 mr-2 text-blue-400" />
                     Advanced Options
                   </h3>
                 </div>
                 <div className="p-4 space-y-4">
                   {/* Salt Option */}
                   <div className="space-y-2">
                     <div className="flex items-center justify-between">
                       <label className="flex items-center cursor-pointer">
                         <input 
                           type="checkbox" 
                           checked={useSalt} 
                           onChange={() => setUseSalt(!useSalt)}
                           className="sr-only peer" 
                         />
                         <div className="relative w-10 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                         <span className="ml-3 text-sm">Use Salt</span>
                       </label>
                       <i className="text-blue-400"><Info className="w-4 h-4" /></i>
                     </div>
                     {useSalt && (
                       <p className="text-xs text-gray-400 italic">
                         A random salt enhances security by generating unique hashes even for identical inputs.
                       </p>
                     )}
                   </div>
                 </div>
               </div>
             </div>
           </div>
     
           {/* Center Panel */}
           <div className="col-span-6">
             <div className="space-y-6">
               {/* Main Input Panel */}
               <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg">
                 <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800 flex justify-between items-center">
                   <h3 className="text-xl font-semibold flex items-center">
                     <Database className="w-5 h-5 mr-2 text-blue-400" />
                     Input Source
                   </h3>
                   <div className="flex space-x-2">
                     <button className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" onClick={handleReset}>
                        <RotateCcw className="w-4 h-4" />
                      </button>
                     <button
                       className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
                       onClick={() => handleCopy(inputText, setIsCopiedInput)}
                     >
                       {isCopiedInput ? (
                         <Check className="w-4 h-4 text-green-500" />
                       ) : (
                         <Copy className="w-4 h-4" />
                       )}
                     </button>
                      <button className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" 
                      onClick={() => handleDownload(hashResult, `${selectedAlgorithm}-Input.txt`

                      )}>
                      
                        <Download className="w-4 h-4" />
                      </button>
                   </div>
                 </div>
                 <div className="p-4">
                   {/* Input Area */}
                   {inputType === 'text' ? (
                     <div>
                       <textarea
                         value={inputText}
                         onChange={(e) => setInputText(e.target.value)}
                         placeholder="Enter text to hash..."
                         className={`w-full h-64 bg-black/30 border ${inputBorderColor} rounded-lg p-4 text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono resize-none`}
                       />
                       <div className="mt-2 text-xs text-gray-500 flex justify-between">
                         <span>{inputText.length} characters</span>
                         <span>{new Blob([inputText]).size} bytes</span>
                       </div>
                     </div>
                   ) : inputType === 'file' ? (
                     <div 
                       className="border-2 border-dashed border-gray-700 rounded-lg h-64 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors cursor-pointer"
                       onDragOver={(e) => e.preventDefault()}
                       onDrop={async (e) => {
                         e.preventDefault();
                         const file = e.dataTransfer.files[0];
                         if (file) {
                           await handleFileChange({ target: { files: [file] } });
                           setInputFileName(file.name);
                         }
                       }}
                       onClick={() => document.getElementById('fileInput').click()}
                     >
                       <div className="text-center">
                         <Upload className="w-12 h-12 mx-auto text-blue-500 mb-4" />
                         <p className="text-gray-400">
                           Drag and drop a file or click to upload
                         </p>
                         {inputFileName && (
                           <div className="mt-3 p-2 bg-gray-800 rounded-lg inline-block">
                             <p className="text-gray-300 flex items-center">
                               <File className="w-4 h-4 mr-2 text-blue-400" />
                               {inputFileName}
                             </p>
                           </div>
                         )}
                         <input 
                           id="fileInput"
                           type="file" 
                           onChange={(e) => {
                             const file = e.target.files[0];
                             if (file) {
                               setInputFileName(file.name);
                               handleFileChange(e);
                             }
                           }}
                           className="hidden"
                         />
                       </div>
                     </div>
                   ) : inputType === 'disk' && (
                     <div className="h-64 flex flex-col">
                       <div className="bg-black/20 rounded-lg p-3 mb-2">
                         <h4 className="text-sm text-gray-400 mb-2 flex items-center">
                           <HardDrive className="w-4 h-4 mr-2 text-blue-400" />
                           Available Disks
                         </h4>
                         <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar">
                           {diskList.map((disk, index) => (
                             <button
                               key={index}
                               onClick={() => selectDisk(disk.device)}
                               className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between text-sm ${
                                 selectedDisk === disk.device ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'
                               }`}
                             >
                               <div className="flex items-center">
                                 <HardDrive className="w-4 h-4 mr-2" />
                                 <span>{disk.device} {disk.label}</span>
                               </div>
                               <div className="flex items-center">
                                 <span className="text-xs px-2 py-1 rounded-full bg-blue-900/40 text-blue-300 border border-blue-700/30">
                                   Select
                                 </span>
                               </div>
                             </button>
                           ))}
                         </div>
                       </div>
                       {selectedDisk && (
                         <div className="bg-blue-900/20 border border-blue-900/30 rounded-lg p-3 mt-2">
                           <p className="text-sm flex items-center">
                             <Check className="w-4 h-4 mr-2 text-green-400" />
                             Selected disk: <span className="font-mono ml-2 text-blue-300">{selectedDisk}</span>
                           </p>
                         </div>
                       )}
                     </div>
                   )}
     
                   {/* Controls */}
                   <div className="mt-4 flex justify-between items-center">
                     <button
                       onClick={submitHashing}
                       disabled={isProcessing}
                       className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-lg transition-all flex items-center space-x-2 shadow-lg disabled:opacity-50"
                     >
                       {isProcessing ? (
                         <>
                           <RefreshCw className="w-5 h-5 animate-spin" />
                           <span>Processing...</span>
                         </>
                       ) : (
                         <>
                           <Hash className="w-5 h-5" />
                           <span>Generate Hash</span>
                         </>
                       )}
                     </button>
                     
                     {errorMessage && (
                       <div className="text-red-400 text-sm flex items-center">
                         <AlertTriangle className="w-4 h-4 mr-2" />
                         {errorMessage}
                       </div>
                     )}
                   </div>
                 </div>
               </div>
     
               {/* Hash Visualization */}
               <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg">
                 <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800">
                   <h3 className="text-lg font-semibold flex items-center">
                     <BarChart className="w-5 h-5 mr-2 text-blue-400" />
                     Hash Visualization
                   </h3>
                 </div>
                 <div className="p-4 flex justify-center">
                    <div className="h-16 w-full bg-black/30 rounded-lg flex items-center justify-evenly px-4">
                      {hashResult && String(hashResult).length > 0 && Array.from(String(hashResult).substring(0, 32)).map((char, index) => {
                        const value = parseInt(char, 16) || 0;
                        const height = `${20 + value * 3}px`;
                        return (
                          <div
                            key={index}
                            style={{ height, width: '3px' }}
                            className="bg-blue-500 rounded-t-sm"
                          ></div>
                        );
                      })}
                      {hashResult && String(hashResult).length === 0 && (
                        <div className="text-gray-500 text-sm">
                          No hash data to visualize
                        </div>
                      )}
                      {!hashResult && (
                        <div className="text-gray-500 text-sm">
                          No hash data to visualize
                        </div>
                      )}
                    </div>
                  </div>
               </div>
             </div>
           </div>
     
           {/* Right Panel */}
           <div className="col-span-3">
             <div className="space-y-6">
               {/* Output Format */}
               <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg">
                 <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800">
                   <h3 className="text-lg font-semibold flex items-center">
                     <FileType className="w-5 h-5 mr-2 text-blue-400" />
                     Output Format
                   </h3>
                 </div>
                 <div className="p-3">
                   <div className="space-y-1">
                     <button
                       onClick={() => setOutputFormat('hex')}
                       className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between ${
                         outputFormat === 'hex' ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-md' : 'hover:bg-gray-800'
                       }`}
                     >
                       <span className="flex items-center">
                         <Code className="w-4 h-4 mr-2" />
                         Hexadecimal
                       </span>
                       {outputFormat === 'hex' && <Check className="w-4 h-4" />}
                     </button>
                     <button
                       onClick={() => setOutputFormat('base64')}
                       className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between ${
                         outputFormat === 'base64' ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-md' : 'hover:bg-gray-800'
                       }`}
                     >
                       <span className="flex items-center">
                         <Code className="w-4 h-4 mr-2" />
                         Base64
                       </span>
                       {outputFormat === 'base64' && <Check className="w-4 h-4" />}
                     </button>
                     <button
                       onClick={() => setOutputFormat('decimal')}
                       className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between ${
                         outputFormat === 'decimal' ? 'bg-gradient-to-r from-blue-600 to-blue-700 shadow-md' : 'hover:bg-gray-800'
                       }`}
                     >
                       <span className="flex items-center">
                         <Code className="w-4 h-4 mr-2" />
                         Decimal Integer
                       </span>
                       {outputFormat === 'decimal' && <Check className="w-4 h-4" />}
                     </button>
                   </div>
                 </div>
               </div>
     
               {/* Hash Output */}
               <div className={`bg-gray-900/60 backdrop-blur-sm border ${outputBorderColor} rounded-xl overflow-hidden shadow-lg`}>
                 <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800 flex justify-between items-center">
                   <h3 className="text-lg font-semibold flex items-center">
                     <Key className="w-5 h-5 mr-2 text-blue-400" />
                     Hash Result
                   </h3>
                   <div className="flex space-x-2">
                   <button className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white" onClick={handleResetOutput}>
                      <RotateCcw className="w-4 h-4" />
                    </button>
                     <button
                       className="p-1 hover:bg-gray-700 rounded-md text-gray-400 hover:text-white transition-colors"
                       onClick={() => handleCopy(hashResult, setIsCopiedOutput)}
                     >
                       {isCopiedOutput ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                     </button>
                     <button className="p-1 hover:bg-gray-700 rounded-md text-gray-400 hover:text-white transition-colors"
                      onClick={() => handleDownload(hashResult, `${selectedAlgorithm}-output.txt
                      `)}>
                      <Download className="w-4 h-4" />
                    </button>
                   </div>
                 </div>
                 <div className="p-4">
                   {isProcessing ? (
                     <div className="animate-pulse space-y-2">
                       <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                       <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                     </div>
                   ) : hashResult ? (
                     <div className="relative">
                       <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 to-indigo-900/10 rounded-lg pointer-events-none"></div>
                       <div className={`break-all bg-black/30 p-4 rounded-lg font-mono text-sm max-h-32 overflow-y-auto custom-scrollbar border border-gray-800`}>
                         <span className={`${textColor}`}>{hashResult}</span>
                       </div>
                     </div>
                   ) : (
                     <div className="flex items-center justify-center py-8">
                       <span className="text-gray-500 text-sm flex items-center">
                         <Hash className="w-4 h-4 mr-2 opacity-50" />
                         No hash generated yet
                       </span>
                     </div>
                   )}
                 </div>
               </div>
     
               {/* Security Summary */}
               <div className="bg-gray-900/60 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-lg">
                 <div className="p-4 bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border-b border-gray-800">
                   <h3 className="text-lg font-semibold flex items-center">
                     <ShieldCheck className="w-5 h-5 mr-2 text-blue-400" />
                     Security Summary
                   </h3>
                 </div>
                 <div className="p-4">
                   <div className="space-y-4">
                     <div className="flex items-center text-sm bg-black/20 p-3 rounded-lg">
                       <div className="w-8 h-8 rounded-full bg-blue-900/30 flex items-center justify-center mr-3">
                         <Lock className="w-4 h-4 text-blue-400" />
                       </div>
                       <div>
                         <p className="text-gray-400 text-xs">Algorithm</p>
                         <p className="text-base font-medium">{selectedAlgorithm}</p>
                       </div>
                     </div>
                     
                     {/* Selected algorithm's security level */}
                     {(() => {                    
                       const selectedAlg = algorithms.find(algorithm => algorithm.id === selectedAlgorithm);
                       const securityColor = 
                         selectedAlg?.security === 'Low' ? 'text-red-400' :
                         selectedAlg?.security === 'Medium' ? 'text-yellow-400' :
                         'text-green-400';
                       
                       const securityBg = 
                         selectedAlg?.security === 'Low' ? 'bg-red-900/30' :
                         selectedAlg?.security === 'Medium' ? 'bg-yellow-900/30' :
                         'bg-green-900/30';
                       
                       const securityIcon = 
                         selectedAlg?.security === 'Low' ? <AlertTriangle className="w-4 h-4 text-red-400" /> :
                         selectedAlg?.security === 'Medium' ? <AlertCircle className="w-4 h-4 text-yellow-400" /> :
                         <ShieldCheck className="w-4 h-4 text-green-400" />;
                       
                       return (
                         <div className="flex items-center text-sm bg-black/20 p-3 rounded-lg">
                           <div className={`w-8 h-8 rounded-full ${securityBg} flex items-center justify-center mr-3`}>
                             {securityIcon}
                           </div>
                           <div>
                             <p className="text-gray-400 text-xs">Security Level</p>
                             <p className={`text-base font-medium ${securityColor}`}>
                               {selectedAlg ? selectedAlg.security : 'N/A'}
                             </p>
                           </div>
                         </div>
                       );
                     })()}
                     
                     {/* Collision Resistance */}
                     <div className="flex items-center text-sm bg-black/20 p-3 rounded-lg">
                       <div className="w-8 h-8 rounded-full bg-yellow-900/30 flex items-center justify-center mr-3">
                         <Shield className="w-4 h-4 text-yellow-400" />
                       </div>
                       <div>
                         <p className="text-gray-400 text-xs">Collision Resistance</p>
                         <p className="text-base font-medium">
                           {
                             (selectedAlgorithm)
                               ? (collisionResistance[selectedAlgorithm] 
                                 ? <span className="text-green-400">Strong</span>
                                 : <span className="text-red-400">Vulnerable</span>)
                               : 'N/A'
                           }
                         </p>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           </div>                      
         </div>         
        )}

      {activeTab === 'hashboard' && (
        <HashVisualizer hashResult={hashResult} algorithm={selectedAlgorithm} />  
      )}

        {activeTab === 'integrity' && (
          <IntegrityBoard />
        )}
        </div>

        {/* Footer */}
        <Footer />
      </div>
      
    </div>
  );
};

export default HashingInterface;